import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, Calendar, ArrowRight } from 'lucide-react';

/**
 * AccountingStartDateModal
 * 
 * A reusable modal that appears when users try to create transactions
 * without first setting their accounting start date.
 * 
 * Usage:
 * ```jsx
 * const [showStartDateModal, setShowStartDateModal] = useState(false);
 * 
 * // In useEffect or before creating transaction:
 * if (!business?.accounting_start_date) {
 *   setShowStartDateModal(true);
 *   return;
 * }
 * 
 * // In JSX:
 * <AccountingStartDateModal 
 *   open={showStartDateModal} 
 *   onClose={() => setShowStartDateModal(false)} 
 * />
 * ```
 */
export function AccountingStartDateModal({ open, onClose, transactionType = 'transaction' }) {
  const navigate = useNavigate();
  
  const handleSetupClick = () => {
    onClose();
    navigate('/settings/start-date');
  };
  
  const getTransactionLabel = () => {
    switch (transactionType) {
      case 'invoice': return 'invoices';
      case 'bill': return 'bills';
      case 'journal': return 'journal entries';
      case 'expense': return 'expenses';
      case 'deferred_revenue': return 'deferred revenue schedules';
      case 'fixed_asset': return 'fixed assets';
      case 'investment': return 'investments';
      case 'loan': return 'loans';
      case 'payroll': return 'payroll';
      case 'budget': return 'budgets';
      default: return 'transactions';
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <Calendar className="w-8 h-8 text-amber-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            Set Your Auto-Capture Start Date
          </DialogTitle>
          <DialogDescription className="text-center space-y-3">
            <p>
              Before you can create {getTransactionLabel()}, 
              you need to choose when we start capturing your financial data.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  This is the date from which we automatically track your transactions, invoices, and bills.
                </span>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        
        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button 
            onClick={handleSetupClick}
            className="w-full gap-2 bg-[#108981] hover:bg-[#0d7069]"
            data-testid="setup-start-date-btn"
          >
            <Calendar className="w-4 h-4" />
            Set Up Start Date
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full"
          >
            I'll do this later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AccountingStartDateModal;
